import { FileLikeObject, FileUploader } from 'ng2-file-upload';
import { Component, ElementRef, EventEmitter, forwardRef, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { isString } from 'micro-dash';
import { SafeUrl } from '@angular/platform-browser';
import { FileItem } from 'ng2-file-upload/file-upload/file-item.class';
import { BookrawAppModule } from 'bookraw/app.module';
import { DefaultImageTypes, FileMedia, MediaType, MimeTypes } from 'bookraw/shared/models/media.model';
import { AppLoaderService } from 'app/shared/services/app-loader/app-loader.service';
import { environment } from 'env/environment';
import { TranslateService } from '@ngx-translate/core';

interface ApiFileUploadItem {
  ng2FileItem: FileItem;
  fileName: string;
  extension: string;
  path: string;
  url: string;
}

@Component({
  selector: 'file-uploader',
  templateUrl: './file-uploader.component.html',
  styleUrls: ['./file-uploader.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileUploaderComponent),
      multi: true
    }
  ]
})
export class FileUploaderComponent implements ControlValueAccessor, OnInit {
  @ViewChild('uploadInput') uploadInputVariable: ElementRef;
  @Input() media?: FileMedia | string |null;
  @Input() label: string = '';
  @Input() dragAndDrop: boolean = true;
  @Input() uploadedFileText?: string = '';
  @Input() showPreview?: boolean = true;
  @Input() enableMultiple?: boolean = false;
  @Input() mediaType: MediaType;
  @Input() enableDeleteButton: boolean = false;
  @Input() disabledNewImage?: boolean = false;
  @Input() buttonText: string = '';
  @Input() addIcon: boolean = false;
  @Input() inputFileUrl: boolean = false;
  @Output() queueLengthChange?: EventEmitter<number> = new EventEmitter();
  @Output() fileNameChange?: EventEmitter<string> = new EventEmitter();
  @Output() uploadSuccess?: EventEmitter<boolean> = new EventEmitter();
  public fileUrl?: string;
  public _value: string | ApiFileUploadItem[];
  public disabled: boolean;
  public uploaderControl: boolean;

  public fileArray: ApiFileUploadItem[] = [];
  public mimeTypes: string;
  public isImageMimeType: boolean;

  public uploader: FileUploader;
  public hasBaseDropZoneOver: boolean = false;
  public previewImageUrl: string | SafeUrl | ArrayBuffer = '';
  protected readonly loaderService: AppLoaderService;

  public fileOverBase(e: any): void {
    this.hasBaseDropZoneOver = e;
  }

  constructor(public translate: TranslateService) {
    this.loaderService = BookrawAppModule.injector.get(AppLoaderService);
  }

  ngOnInit() {
    this.uploader = new FileUploader({
      url: environment.baseURL + '/api/media',
      headers: [
        // csak json választ akarunk ezért küldjük az accept headert
        {name: 'Accept', value: 'application/json'}
      ],
      autoUpload: false,
      method: 'post',
      itemAlias: 'media',
      additionalParameter: {
        'type': this.mediaType
      },
      queueLimit: this.enableMultiple ? undefined : 1
    });
    this.addToDropZone();
    this.updateFileUrl();
    this.mimeTypes = MimeTypes.find(mimeType => mimeType.mediaType === this.mediaType)?.mime.join();
    this.isImageMimeType = DefaultImageTypes.join().includes(this.mimeTypes)
  }

  private updateFileUrl() {
    if (this.media && typeof this.media !== 'string') {
      this.fileUrl = this.media.original || this.media.optimal || this.media.original_xl || this.media.original_s || this.media.landscape_m;
    } else if (this.media && typeof this.media === 'string') {
      this.fileUrl = this.media;
    }
  }

  // visszaadja a fájl nevét kiterjesztés nélkül
  private getFileName(file: File | FileLikeObject): string {
    return file.name.replace(/\.[^/.]+$/, '');
  }

  public removeFromQueue(file: FileItem) {

    // a controlból is törölni kell az értékeket
    const itemToRemove = this.fileArray.find((i) => i.ng2FileItem === file);
    const index = this.fileArray.indexOf(itemToRemove);
    if (index >= 0) {
      this.fileArray.splice(index, 1);
    }

    file.remove();
    // törlésnél is jelezzük, hogy változott a queue limit
    this.queueLengthChange.emit(this.uploader.queue.length);
    this.refreshInputValue();
  }

  addToDropZone() {
    // ha csak egyet lehet feltölteni, akkor automatikusan felülírjuk az előzőt
    if (!this.enableMultiple) {
      this.uploader.removeFromQueue = () => {
        this.uploader.queue = [];
        this.uploadInputVariable.nativeElement.value = '';
        this.previewImageUrl = '';
      };
    }

    // új fájlnál küldjük az eventet az aktuális fájlok számával
    this.uploader.onAfterAddingAll = () => {
      this.loaderService.open( this.translate.instant('file_upload.in_progress') );
      this.queueLengthChange.emit(this.uploader.queue.length);
    };

    // hibaüzenet megjelenítés ha nem menne a feltöltés
    this.uploader.onErrorItem = (item: FileItem, response: string, status: number) => {
      if (status === 413) {
        BookrawAppModule.showErrorMessage( this.translate.instant('file_upload.max_size_error') );
      } else {
        BookrawAppModule.showErrorMessage(`Error: ${response || status}`);
      }
      this.removeFromQueue(item);
      this.loaderService.close();
    };

    this.uploader.onCompleteItem = (item: FileItem, response: string) => {
      try {
        const json = JSON.parse(response);
        const fileObject: ApiFileUploadItem = {
          ng2FileItem: item,
          fileName: this.getFileName(item.file),
          path: json.result.path,
          extension: json.result.path.match(/\.([0-9a-z]+)(?=[?#])|(\.)(?:[\w]+)$/gmi)?.pop(),
          url: json.result.urls?.original
        };

        // ha nem támogatott a multiple akkor csak egy elemes tömb lesz a fileArray
        if (!this.enableMultiple) {
          this.fileArray = [fileObject];
        } else {
          this.fileArray.push(fileObject);
        }

        this.refreshInputValue();
        this.uploadSuccess.emit(true);
        this.loaderService.close();
      } catch {
        //
      }
    };

    this.uploader.onAfterAddingFile = (file) => {
      this.fileNameChange.emit(this.getFileName(file.file));
      this.uploaderControl = true;
      file.withCredentials = false;

      // Automatikus feltöltés fájl behúzásakor
      if (!file.isReady && !file.isUploading && !file.isSuccess) {
        file.upload();
      }
    };
  }

  deleteSingleFile() {
    this.fileArray.splice(0, 1);
    this.queueLengthChange.emit(this.uploader.queue.length);
    this.refreshInputValue();
  }

  onChanged: any = () => {};
  onTouched: any = () => {};

  writeValue(val) {
    if (isString(val)) {
      this._value = val;
    } else if (val === null) {
      this._value = this.fileArray = this.fileUrl = null;
    }
  }

  registerOnChange(fn: any) {
    this.onChanged = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  getInputValue(): null | string | ApiFileUploadItem[] {
    if (this.uploader.queue.length > 1) {
      // tömb a result több elemnél
      return this.fileArray;
    } else {
      // ha egy elem van akkor string a result
      return this.fileArray[0]?.path || null;
    }
  }

  refreshInputValue() {
    if (!this.disabled) {
      const value = this.getInputValue();
      this._value = value;

      // ha csak egy elem van akkor beállítjuk a letöltő linket
      if (this.uploader.queue.length === 1 && !this.inputFileUrl) {
        this.fileUrl = this.fileArray[0].url;
      } else if (this.uploader.queue.length !== 1 && !this.inputFileUrl) {
        this.fileUrl = null;
      }

      // feltöltés után ha nincs multi akkor automatikusan törlődik a beállított fájl,
      // így felülirható a legközelebbi dragdroppal
      if (!this.enableMultiple) {
        this.uploader.queue = [];
      }

      this.previewImageUrl = '';
      this.uploadInputVariable.nativeElement.value = '';
      this.uploaderControl = false;
      this.onChanged(value);
      this.onTouched();
    }
  }
}
